import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMe } from '~/api/queries';
import { appLock, type Biometry, getBiometry, useAppLocked, verifyBiometric } from '~/lib/biometric';
import { session } from '~/session/session';
import { FaceIdIcon, FingerprintIcon } from './icons';
import { Avatar, PrimaryButton, RingLogo } from './ui';

/** Covers the app until Face ID / fingerprint clears it (native apps, when turned on in Me). */
export function AppLockScreen() {
  const locked = useAppLocked();
  if (!locked) return null;
  return <LockScreen />;
}

function LockScreen() {
  const navigate = useNavigate();
  const me = useMe().data;
  const [kind, setKind] = useState<Biometry | null>(null);
  const [busy, setBusy] = useState(false);
  const asked = useRef(false);

  const unlock = useCallback(async () => {
    setBusy(true);
    const ok = await verifyBiometric('Unlock Hudhuria');
    setBusy(false);
    if (ok) appLock.unlock();
  }, []);

  useEffect(() => {
    void getBiometry().then((b) => {
      setKind(b);
      if (!b)
        appLock.unlock(); // biometrics removed from the device: nothing to unlock with
      else if (!asked.current) {
        asked.current = true;
        void unlock();
      }
    });
  }, [unlock]);

  const label = kind?.label ?? 'Face ID';
  return (
    <div className="absolute inset-0 z-[100] flex animate-fade-in-fast flex-col items-center bg-white px-8 pt-safe-6 pb-safe text-center">
      <div className="mt-[18vh] flex flex-col items-center">
        <RingLogo size={46} />
        {me && (
          <div className="mt-8">
            <Avatar name={me.staff.name} size={64} radius="full" color="#0369a1" fontSize={21} display />
          </div>
        )}
        <div className="mt-4 font-display text-[23px] font-bold">
          {me ? `Welcome back, ${me.staff.name.split(' ')[0]}` : 'Hudhuria is locked'}
        </div>
        <div className="mt-1.5 text-[14px] text-slate-500">Use {label} to open Hudhuria.</div>
      </div>
      <div className="mt-auto flex w-full flex-col gap-2">
        <PrimaryButton loading={busy} onClick={() => void unlock()}>
          {kind?.face === false ? <FingerprintIcon /> : <FaceIdIcon />}
          Unlock with {label}
        </PrimaryButton>
        <button
          type="button"
          onClick={() => {
            // Keep queued work: it syncs after they sign in again.
            session.expire();
            appLock.unlock();
            void navigate({ to: '/sign-in', replace: true });
          }}
          className="cursor-pointer border-none bg-transparent py-3 text-[13.5px] font-semibold text-slate-500"
        >
          Sign in with password instead
        </button>
      </div>
    </div>
  );
}
